<script setup lang="ts">

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { AppText, AppTextLink } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { BtnIcon } from '@shared/ui/buttons'

import ChangelogItemActions from './ChangelogItemActions.vue'
import ChangelogCursorPagination from './ChangelogCursorPagination.vue'
import { useTableEntityChangelog } from '../model/table-entity-changelog.hook'

defineOptions({
  name: 'TableEntityChangelog',
})

const {
  actions,
  isLoading,
  hasActions,
  hasNext,
  hasPrev,
  perPage,
  count,
  expandedActionId,
  items,
  isItemsLoading,
  formatDateTime,
  getActionLabel,
  getItemChange,
  getItemInstance,
  getUserLabel,
  getUserLink,
  toggleItems,
  openItemDetail,
  setChangePerPage,
  goNext,
  goPrev,
} = useTableEntityChangelog()
</script>

<template>
  <div class="table-entity-changelog">
    <ChangelogCursorPagination
      :per-page="perPage"
      :count="count"
      :has-prev="hasPrev"
      :has-next="hasNext"
      :disabled="isLoading"
      :show-title="false"
      @change:per-page="setChangePerPage"
      @prev="goPrev"
      @next="goNext"
    />

    <AppTable
      class="rounded-table"
      :loading="isLoading"
      :paginated="false"
    >
      <template #head>
        <AppTr>
          <AppTh class="table-entity-changelog__id-cell">
            <AppText name="body1-medium">
              #
            </AppText>
          </AppTh>
          <AppTh class="table-entity-changelog__date-cell">
            <AppText name="body1-medium">
              Date &amp; time
            </AppText>
          </AppTh>
          <AppTh>
            <AppText name="body1-medium">
              Action
            </AppText>
          </AppTh>
          <AppTh>
            <AppText name="body1-medium">
              Context
            </AppText>
          </AppTh>
          <AppTh>
            <AppText name="body1-medium">
              User
            </AppText>
          </AppTh>
          <AppTh class="table-entity-changelog__toggle-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasActions"
        #body
      >
        <template
          v-for="row in actions"
          :key="row.id"
        >
          <AppTr>
            <AppTd class="medium table-entity-changelog__id-cell">
              <AppText name="body1">
                {{ row.id }}
              </AppText>
            </AppTd>
            <AppTd class="medium table-entity-changelog__date-cell">
              <AppText name="body1">
                {{ formatDateTime(row.created_at) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText name="body1">
                {{ getActionLabel(row) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText name="body1">
                {{ row.context }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppTextLink
                v-if="getUserLink(row)"
                name="body1"
                color="blue-700"
                :href="getUserLink(row) ?? ''"
              >
                {{ getUserLabel(row) }}
              </AppTextLink>
              <AppText
                v-else
                name="body1"
              >
                {{ getUserLabel(row) }}
              </AppText>
            </AppTd>
            <AppTd class="zero-padding table-entity-changelog__toggle-cell">
              <BtnIcon
                :class="['table-entity-changelog__toggle', { 'table-entity-changelog__toggle--open': expandedActionId === row.id }]"
                :icon="faChevronDown"
                view="table"
                color="black"
                title="Toggle details"
                @click="toggleItems(row)"
              />
            </AppTd>
          </AppTr>

          <AppTr v-if="expandedActionId === row.id">
            <AppTd
              class="zero-padding"
              :colspan="6"
            >
              <div class="table-entity-changelog__details">
                <AppText
                  v-if="isItemsLoading"
                  name="body2"
                  color="slate-500"
                >
                  Loading…
                </AppText>

                <AppTable
                  v-else
                  class="rounded-table"
                  :paginated="false"
                >
                  <template #head>
                    <AppTr>
                      <AppTh class="table-entity-changelog__item-id-cell">
                        <AppText name="body1-medium">
                          #
                        </AppText>
                      </AppTh>
                      <AppTh class="table-entity-changelog__item-date-cell">
                        <AppText name="body1-medium">
                          Date &amp; time
                        </AppText>
                      </AppTh>
                      <AppTh class="table-entity-changelog__item-instance-cell">
                        <AppText name="body1-medium">
                          Instance #id
                        </AppText>
                      </AppTh>
                      <AppTh>
                        <AppText name="body1-medium">
                          Action
                        </AppText>
                      </AppTh>
                      <AppTh class="table-entity-changelog__toggle-cell" />
                    </AppTr>
                  </template>
                  <template #body>
                    <AppTr
                      v-for="item in items"
                      :key="item.id"
                    >
                      <AppTd class="medium table-entity-changelog__item-id-cell">
                        <AppText name="body1">
                          {{ item.id }}
                        </AppText>
                      </AppTd>
                      <AppTd class="medium">
                        <AppText name="body1">
                          {{ formatDateTime(item.created_at) }}
                        </AppText>
                      </AppTd>
                      <AppTd class="medium">
                        <AppText name="body1">
                          {{ getItemInstance(item) }}
                        </AppText>
                      </AppTd>
                      <AppTd class="medium">
                        <AppText name="body1">
                          {{ getItemChange(item) }}
                        </AppText>
                      </AppTd>
                      <AppTd class="zero-padding table-entity-changelog__toggle-cell">
                        <ChangelogItemActions @click:view="openItemDetail(item)" />
                      </AppTd>
                    </AppTr>
                  </template>
                </AppTable>
              </div>
            </AppTd>
          </AppTr>
        </template>
      </template>
    </AppTable>

    <ChangelogCursorPagination
      :per-page="perPage"
      :count="count"
      :has-prev="hasPrev"
      :has-next="hasNext"
      :disabled="isLoading"
      :show-title="false"
      @change:per-page="setChangePerPage"
      @prev="goPrev"
      @next="goNext"
    />
  </div>
</template>

<style scoped lang="scss" src="./TableEntityChangelog.scss" />
